import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

const Header = ({ activeSection, setActiveSection }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navItems = [
    { id: "home", label: t("nav.home"), path: "/", isSection: true },
    { id: "about", label: t("nav.about"), path: "/#about", isSection: true },
    {
      id: "partners",
      label: t("nav.partners"),
      path: "/#partners",
      isSection: true,
    },
    {
      id: "activities",
      label: t("nav.activities"),
      path: "/activities",
      isSection: false,
    },
    {
      id: "resources",
      label: t("nav.resources"),
      path: "/resources",
      isSection: false,
    },
    { id: "news", label: t("nav.news"), path: "/news", isSection: false },
    { id: "events", label: t("nav.events"), path: "/events", isSection: false },
    { id: "media", label: t("nav.media"), path: "/media", isSection: false },
    {
      id: "contact",
      label: t("nav.contact"),
      path: "/contact",
      isSection: false,
    },
  ];

  return (
    <header
      className={`fixed w-full z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-white/95 backdrop-blur-md shadow-lg"
          : "bg-white shadow-sm"
      }`}
    >
      <div className="container mx-auto px-4 py-4 flex justify-between items-center">
        <div className="w-full flex justify-between items-center space-x-6">
          {/* Logo with animation */}
          <div className="flex-shrink-0 group">
            <Link to="/" onClick={() => setActiveSection("home")}>
              <img
                src="/dca-logo.png"
                alt="DCA Project Logo"
                className="h-10 w-auto transition-transform duration-300 group-hover:scale-110 cursor-pointer"
                loading="eager"
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-1">
            {navItems.map((item) => (
              <Link
                key={item.id}
                to={item.path}
                onClick={(e) => {
                  if (item.isSection) {
                    // If already on home page and clicking the same section, prevent scroll
                    if (
                      location.pathname === "/" &&
                      activeSection === item.id
                    ) {
                      e.preventDefault();
                    } else if (location.pathname === "/") {
                      // If on home page but different section, scroll to it
                      e.preventDefault();
                      const element = document.getElementById(item.id);
                      if (element) {
                        element.scrollIntoView({ behavior: "smooth" });
                        setActiveSection(item.id);
                      }
                    } else {
                      // If not on home page, let the default behavior handle navigation
                      setActiveSection(item.id);
                    }
                  } else {
                    // For page navigation, set the active section
                    setActiveSection(item.id);
                  }
                }}
                className={`relative px-4 py-2 text-sm font-medium transition-all duration-300 rounded-lg group ${
                  (item.isSection && activeSection === item.id) ||
                  (!item.isSection && location.pathname === item.path)
                    ? "text-emerald-600 bg-emerald-50"
                    : "text-gray-600 hover:text-emerald-600 hover:bg-emerald-50"
                }`}
              >
                {item.label}
                {((item.isSection && activeSection === item.id) ||
                  (!item.isSection && location.pathname === item.path)) && (
                  <div className="absolute bottom-0 left-0 w-full h-0.5 bg-emerald-600 rounded-full animate-pulse"></div>
                )}
                <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/10 to-teal-500/10 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </Link>
            ))}
          </nav>
        </div>

        {/* Mobile menu button with animation */}
        <div className="md:hidden">
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="relative p-2 text-gray-600 hover:text-emerald-600 focus:outline-none focus:text-emerald-600 transition-colors duration-200"
            aria-label="Toggle menu"
          >
            <div className="w-6 h-6 flex flex-col justify-center items-center">
              <span
                className={`block w-6 h-0.5 bg-current transition-all duration-300 ${
                  isMenuOpen ? "rotate-45 translate-y-1" : "-translate-y-1"
                }`}
              ></span>
              <span
                className={`block w-6 h-0.5 bg-current transition-all duration-300 ${
                  isMenuOpen ? "opacity-0" : "opacity-100"
                }`}
              ></span>
              <span
                className={`block w-6 h-0.5 bg-current transition-all duration-300 ${
                  isMenuOpen ? "-rotate-45 -translate-y-1" : "translate-y-1"
                }`}
              ></span>
            </div>
          </button>
        </div>
      </div>

      {/* Mobile Navigation with slide animation */}
      <div
        className={`md:hidden transition-all duration-300 ease-in-out ${
          isMenuOpen
            ? "max-h-96 opacity-100"
            : "max-h-0 opacity-0 overflow-hidden"
        }`}
      >
        <nav className="bg-white/95 backdrop-blur-md shadow-lg">
          <div className="container mx-auto px-4 py-3">
            <div className="flex flex-col space-y-2">
              {navItems.map((item) => (
                <Link
                  key={item.id}
                  to={item.path}
                  onClick={(e) => {
                    if (item.isSection) {
                      // If already on home page and clicking the same section, prevent scroll
                      if (
                        location.pathname === "/" &&
                        activeSection === item.id
                      ) {
                        e.preventDefault();
                      } else if (location.pathname === "/") {
                        // If on home page but different section, scroll to it
                        e.preventDefault();
                        const element = document.getElementById(item.id);
                        if (element) {
                          element.scrollIntoView({ behavior: "smooth" });
                          setActiveSection(item.id);
                        }
                      } else {
                        // If not on home page, let the default behavior handle navigation
                        setActiveSection(item.id);
                      }
                    } else {
                      // For page navigation, set the active section
                      setActiveSection(item.id);
                    }
                    setIsMenuOpen(false);
                  }}
                  className={`relative px-4 py-3 text-sm font-medium transition-all duration-300 rounded-lg group ${
                    (item.isSection && activeSection === item.id) ||
                    (!item.isSection && location.pathname === item.path)
                      ? "text-emerald-600 bg-emerald-50"
                      : "text-gray-600 hover:text-emerald-600 hover:bg-emerald-50"
                  }`}
                >
                  {item.label}
                  <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/10 to-teal-500/10 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </Link>
              ))}
            </div>
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Header;
